import React from 'react';
import BusinessEmailPackages from '../components/email/BusinessEmailPackages';
import SEOHelmet from '../components/SEOHelmet';

const Email: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Business Email Hosting",
    "description": "Professional business email hosting with your domain",
    "brand": {
      "@type": "Brand",
      "name": "12and22"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "price": "14.99"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet
        title="Business Email Hosting - Professional Email Solutions | 12and22"
        description="Professional business email on your own domain. 10 to 50 GB per mailbox, webmail, spam filtering, one-click setup for Outlook, Apple and Thunderbird, and free migration from your old provider. From $14.99/month for 5 mailboxes."
        keywords="business email, professional email, email hosting, custom email, corporate email, email with domain, webmail, email security, 12and22"
        canonicalUrl="https://12and22.com/email"
        structuredData={structuredData}
      />
      {/* Hero Section with modern design */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full text-gray-700">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium">Professional Communication</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Business</span> <span className="text-primary-navy">Email</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-4xl mx-auto">
              Email on your own domain, hosted on our dedicated mail platform.
              Works in every mail app, backed up hourly, with spam filtering and SPF, DKIM and DMARC set up for you.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <a
                href="#packages"
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">Choose Email Plan</span>
              </a>
              
              <a
                href="#features"
                className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gradient-to-r hover:from-orange-400 hover:to-yellow-500 hover:text-white hover:border-transparent transition-all duration-200"
              >
                See Features
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-sm max-w-4xl mx-auto">
              {[
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>, 
                  text: 'Custom Domain', 
                  subtext: 'your@company.com' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>, 
                  text: 'Spam Filtering', 
                  subtext: 'SPF, DKIM & DMARC included' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>, 
                  text: 'Any Mail App', 
                  subtext: 'Outlook, Apple, Thunderbird, phones' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>, 
                  text: 'Free Migration', 
                  subtext: 'Bring mail from your old provider' 
                }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${index === 0 || index === 3 ? 'bg-gradient-to-br from-orange-400 to-yellow-500' : 'bg-gray-100'} rounded-lg flex items-center justify-center ${index === 0 || index === 3 ? 'text-white' : 'text-gray-600'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.text}</div>
                    <div className="text-xs text-gray-500">{item.subtext}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <div id="packages">
        <BusinessEmailPackages />
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">What You Get</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Powerful Email Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything a business needs from email, with nothing you have to set up yourself
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                title: 'Custom Domain Email',
                description: 'Create professional email addresses with your own domain (you@yourcompany.com)'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
                title: 'Spam Filtering & Authentication',
                description: 'Server-side spam filtering, plus SPF, DKIM and DMARC configured for your domain so your mail reaches the inbox'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>,
                title: 'Up to 50 GB per Mailbox',
                description: '10 GB, 25 GB or 50 GB for every mailbox depending on plan, with live usage shown in your dashboard'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>,
                title: 'Works on Every Device',
                description: 'IMAP and SMTP over TLS, one-click setup for Outlook, Thunderbird and Apple devices, and a guide for every major mail app'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
                title: 'Webmail Included',
                description: 'Read and send from any browser at inbox.12and22.com, with nothing to install'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
                title: 'Aliases & Distribution Groups',
                description: 'Point addresses like sales@ at one person or a whole team, and send as the alias from your mailbox (Business and Enterprise plans)'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
                title: 'Attachments up to 50 MB',
                description: 'Messages up to 50 MB each, which is about 35 MB of attachments'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
                title: 'Hourly Off-site Backups',
                description: 'Every mailbox is backed up every hour to encrypted off-site storage'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                title: 'Free Migration Tool',
                description: 'Move existing mailboxes from Google, Microsoft, Zoho or any IMAP provider with the built-in migration tool'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 relative overflow-hidden group">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl group-hover:from-white/30 transition-all duration-200"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                        <div className="text-white relative z-10">
                          {feature.icon}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Features Grid */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Built for Administrators
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
                  title: 'Auto-Responder',
                  description: 'Out-of-office replies per mailbox, with start and end dates'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>,
                  title: 'Usage & Delivery Reports',
                  description: 'Storage per mailbox, last login and a delivery log'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
                  title: 'Reputation Protection',
                  description: 'Bounce and complaint monitoring per domain keeps your sending reputation clean'
                },
                {
                  icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>,
                  title: 'Password Policy & Bulk Setup',
                  description: 'Strong passwords enforced, a generator, and CSV bulk mailbox creation'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center text-gray-600 mb-4">
                    {feature.icon}
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Email;